import React, { useEffect, useMemo, useState } from 'react';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { getRecommendations, getVocabRadar, VocabRadarResponse, VocabRecommendation } from '../utils/api';

const AXES = ['TECH', 'DAILY', 'ACADEMIC', 'BUSINESS', 'CULTURE', 'TRAVEL'];

function normalizeRecommendations(
  payload: VocabRecommendation[] | { items?: VocabRecommendation[]; recommendations?: VocabRecommendation[] }
) {
  if (Array.isArray(payload)) {
    return payload;
  }
  return payload.items || payload.recommendations || [];
}

export default function RadarPage() {
  const [radar, setRadar] = useState<VocabRadarResponse | null>(null);
  const [recommendations, setRecommendations] = useState<VocabRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    Promise.allSettled([getVocabRadar(), getRecommendations()])
      .then(([radarResult, recommendationsResult]) => {
        if (!active) {
          return;
        }
        if (radarResult.status === 'fulfilled') {
          setRadar(radarResult.value);
        }
        if (recommendationsResult.status === 'fulfilled') {
          setRecommendations(normalizeRecommendations(recommendationsResult.value));
        }
        if (radarResult.status === 'rejected' && recommendationsResult.status === 'rejected') {
          setError(radarResult.reason?.message || 'Unable to load radar data.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const domainValues = useMemo(() => {
    const source = radar?.domains || {};
    const normalized = Object.entries(source).reduce<Record<string, number>>((accumulator, [key, value]) => {
      accumulator[key.toLowerCase()] = value;
      return accumulator;
    }, {});
    const values = AXES.map((axis) => normalized[axis.toLowerCase()] ?? 0);
    const max = Math.max(100, ...values);
    return { values, max };
  }, [radar]);

  const polygonPoints = useMemo(() => {
    const centerX = 180;
    const centerY = 170;
    const radius = 110;
    return domainValues.values
      .map((value, index) => {
        const angle = (Math.PI * 2 * index) / AXES.length - Math.PI / 2;
        const scaledRadius = (value / domainValues.max) * radius;
        const x = centerX + scaledRadius * Math.cos(angle);
        const y = centerY + scaledRadius * Math.sin(angle);
        return `${x},${y}`;
      })
      .join(' ');
  }, [domainValues]);

  const gridPolygons = [0.25, 0.5, 0.75, 1].map((scale) => {
    const centerX = 180;
    const centerY = 170;
    const radius = 110 * scale;
    return AXES.map((_, index) => {
      const angle = (Math.PI * 2 * index) / AXES.length - Math.PI / 2;
      return `${centerX + radius * Math.cos(angle)},${centerY + radius * Math.sin(angle)}`;
    }).join(' ');
  });

  const levelDistribution = radar?.level_distribution || { advanced: 42, intermediate: 38, basic: 20 };
  const focusWords = (radar?.recommendations && radar.recommendations.length ? radar.recommendations : recommendations).slice(0, 5);

  return (
    <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="flex-start">
      <Stack spacing={3} sx={{ flex: 1.6, width: '100%' }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {loading ? (
              <Stack alignItems="center" py={8}><CircularProgress /></Stack>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3.5} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>Vocabulary Radar</Typography>
                    <Chip label="LIVE DATA" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }} />
                  </Stack>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>Your linguistic footprint across 6 key domains.</Typography>
                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <svg width="100%" viewBox="0 0 360 340" role="img" aria-label="Vocabulary radar chart">
                      {gridPolygons.map((points) => (
                        <polygon key={points} points={points} fill="none" stroke="#e5e7eb" strokeWidth="1.5" />
                      ))}
                      {AXES.map((axis, index) => {
                        const angle = (Math.PI * 2 * index) / AXES.length - Math.PI / 2;
                        const outerX = 180 + 128 * Math.cos(angle);
                        const outerY = 170 + 128 * Math.sin(angle);
                        return (
                          <g key={axis}>
                            <line x1="180" y1="170" x2={outerX} y2={outerY} stroke="#e5e7eb" strokeWidth="1.5" />
                            <text x={outerX} y={outerY} textAnchor="middle" dominantBaseline="middle" fill="#334155" fontSize="12" fontWeight="700">
                              {axis}
                            </text>
                          </g>
                        );
                      })}
                      <polygon points={polygonPoints} fill="rgba(37, 99, 235, 0.24)" stroke="#2563eb" strokeWidth="3" />
                      {domainValues.values.map((value, index) => {
                        const angle = (Math.PI * 2 * index) / AXES.length - Math.PI / 2;
                        const scaledRadius = (value / domainValues.max) * 110;
                        const x = 180 + scaledRadius * Math.cos(angle);
                        const y = 170 + scaledRadius * Math.sin(angle);
                        return <circle key={`${value}-${index}`} cx={x} cy={y} r="5" fill="#2563eb" />;
                      })}
                    </svg>
                  </Box>
                </Box>

                <Stack spacing={2.5} sx={{ width: { xs: '100%', lg: 280 } }}>
                  {[
                    { label: 'Advanced', value: levelDistribution.advanced || 0, color: '#2563eb' },
                    { label: 'Intermediate', value: levelDistribution.intermediate || 0, color: '#0891b2' },
                    { label: 'Basic', value: levelDistribution.basic || 0, color: '#94a3b8' },
                  ].map((item) => (
                    <Box key={item.label}>
                      <Stack direction="row" justifyContent="space-between" mb={1}>
                        <Typography sx={{ fontWeight: 700 }}>{item.label}</Typography>
                        <Typography color="text.secondary">{item.value}%</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={item.value}
                        sx={{ height: 10, borderRadius: 999, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: item.color } }}
                      />
                    </Box>
                  ))}
                  <Box sx={{ p: 2.5, borderRadius: 3, bgcolor: '#111827', color: '#fff' }}>
                    <Typography variant="h3" sx={{ fontWeight: 800 }}>{radar?.total_words || 248}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>TOTAL WORDS QUERIED</Typography>
                  </Box>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction="row" spacing={1.2} alignItems="center" mb={2.5}>
              <WarningAmberRoundedIcon sx={{ color: '#d97706' }} />
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Focus Required: {radar?.weakest_domain || 'Culture'}
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              {focusWords.map((item, index) => (
                <Box key={`${item.word}-${index}`} sx={{ p: 2.25, borderRadius: 2.5, borderLeft: '4px solid #2563eb', bgcolor: '#f8fafc' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{item.word}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 1 }}>{item.definition || item.reason || 'Recommended to strengthen a lower-confidence domain.'}</Typography>
                  <Chip label={item.domain || item.topic || radar?.weakest_domain || 'Focus'} size="small" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8' }} />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ flex: 1, width: '100%', borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack direction="row" spacing={1.2} alignItems="center" mb={3}>
            <AccessTimeRoundedIcon sx={{ color: '#1a73e8' }} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Recent Activity</Typography>
          </Stack>
          <Stack spacing={2.5}>
            {[
              { icon: <CheckCircleRoundedIcon sx={{ color: '#16a34a' }} />, title: "Mastered 'Scalability'", detail: 'Moved from Intermediate to Advanced.', time: '2 HOURS AGO' },
              { icon: <SearchRoundedIcon sx={{ color: '#2563eb' }} />, title: "Queried 'Ephemeral'", detail: "Added to 'Literature' study list.", time: '5 HOURS AGO' },
              { icon: <AccessTimeRoundedIcon sx={{ color: '#7c3aed' }} />, title: 'Practice Session: Business', detail: '15 words reviewed, 90% accuracy.', time: 'YESTERDAY' },
              { icon: <TrendingDownRoundedIcon sx={{ color: '#ef4444' }} />, title: 'Domain Decay: Culture', detail: 'Retention dropped by 12% this week.', time: '2 DAYS AGO' },
            ].map((item) => (
              <Box key={item.title} sx={{ p: 2.25, borderRadius: 2.5, bgcolor: '#f8fafc' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  {item.icon}
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>{item.title}</Typography>
                    <Typography color="text.secondary">{item.detail}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>{item.time}</Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Button variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ mt: 3, bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}>
            VIEW FULL HISTORY
          </Button>
        </CardContent>
      </Card>
    </Stack>
  );
}
